import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Card, CardContent, Grid, MenuItem, Tooltip, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { z } from 'zod';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { useUpdateDefaultVisitFinishSetting } from '@/features/settings/hooks/mutation/use-update-default-visit-finish-setting';
import { useOrganizationDetails } from '@/features/settings/hooks/query/use-organization-details';
import { VisitFinishSetting } from '@/features/visits/api/types/visits';
import { Can } from '@/permissions/can';

interface FormValues {
  visitFinishSetting: VisitFinishSetting | null;
}

const defaultValues: FormValues = {
  visitFinishSetting: null,
};

const finishVisitOptions = [
  {
    title: 'settings:default.visits.time_ends' as const,
    value: VisitFinishSetting.TIME_ENDS,
  },
  {
    title: 'settings:default.visits.visitors_left' as const,
    value: VisitFinishSetting.VISITORS_LEFT,
  },
];

export const VisitSettings = () => {
  const { t } = useTranslation(['settings', 'common']);

  const { data, error, isLoading } = useOrganizationDetails();

  const { mutate: updateDefaultVisitFinishSetting, isPending } =
    useUpdateDefaultVisitFinishSetting();

  const validationSchema = z.object({
    visitFinishSetting: z.nativeEnum(VisitFinishSetting),
  });

  const {
    control,
    reset,
    formState: { isValid, isDirty },
    handleSubmit,
  } = useForm({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    updateDefaultVisitFinishSetting(data.visitFinishSetting || VisitFinishSetting.TIME_ENDS);
  };

  useEffect(() => {
    if (data?.visitFinishSetting) {
      reset({
        visitFinishSetting: data?.visitFinishSetting,
      });
    }
  }, [data?.visitFinishSetting]);

  const renderContent = () => {
    if (!data) {
      return null;
    }

    return (
      <>
        <Controller
          control={control}
          name="visitFinishSetting"
          render={({ field, fieldState: { error } }) => (
            <InputField
              {...field}
              error={!!error?.message}
              fullWidth
              helperText={error?.message}
              label={t('settings:default.visits.visitFinishSetting')}
              name="visitFinishSetting"
              select
              required>
              {finishVisitOptions?.map(finishReason => (
                <MenuItem key={finishReason.value} value={finishReason.value}>
                  {t(finishReason.title)}
                </MenuItem>
              ))}
            </InputField>
          )}
        />
        <Box
          sx={{
            my: 2,
          }}>
          <Can I="update" a="DefaultSettings" passThrough>
            {allowed => (
              <Tooltip
                title={t('common:notSufficientPermissions')}
                disableHoverListener={allowed}
                disableFocusListener={allowed}>
                <span>
                  <LoadingButton
                    variant="contained"
                    disabled={!isValid || !isDirty || !allowed}
                    loading={isPending}
                    onClick={handleSubmit(onSubmit)}>
                    {t('common:button.save')}
                  </LoadingButton>
                </span>
              </Tooltip>
            )}
          </Can>
        </Box>
      </>
    );
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={4}>
          <Grid item md={5} xs={12}>
            <Typography color="textPrimary" variant="h6">
              {t('settings:default.visits.title')}
            </Typography>
          </Grid>
          <Grid item md={7} xs={12}>
            {renderContent()}
            <DataStatusIndicator
              dataLength={data ? 1 : 0}
              error={error?.response?.data?.message}
              isFetching={isLoading}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
